import classNames from 'classnames';
import { useState, useCallback, useEffect, useRef } from 'react';
import Markdown from 'react-markdown';

import { useFirebaseAuth } from '../../../../../global/FirebaseProvider/FirebaseProvider';
import { useSubscription } from '../../SubscriptionProvider';
import { createSubscriptionCheckoutSession, createOrderCheckoutSession } from '../../../../utils/service';
import { getGenericError } from '../../../../utils/errors';
import CouponCodeInput from '../../../../global/CouponCodeInput';
import StripeCustomCheckout from '../../../../global/StripeElements/StripeCustomCheckout';
import {
  SUBSCRIPTION_STEPS,
  getSubscriptionOrderItemsFromStorage,
  SUBSCRIPTION_PAYMENT_TYPES,
  getOrderSubtotal,
} from '../../../../utils/subscriptions';
import {
  SUBSCRIPTION_TRACKER_TYPES,
  TRACKER_IDS,
  getTrackerIdEventName,
} from '../../../../config/tracker';
import { MEMBERSHIP_TERMS_URL } from '../../../../config/externalURL';
import MembershipOrderTbbContainer from '../../../MembershipOrderTbb/MembershipOrderTbbContainer';
import { useSubscriptionStepScroll } from '../../../../utils/subscriptionFlow';
import MEMBERSHIP_OFFERED_ITEMS, {
  getFilteredMembershipItems,
  MEMBERSHIP_DEFAULT_ORDER_ITEMS,
  MEGA_KIT_MEMBERSHIP_ORDER_ITEMS,
  validateShipment,
} from '../../../../utils/membership';
import { isValidAddress } from '../../../../utils/addresses';
import { RECYCLING_PRODUCT_TYPES } from '../../../../utils/tbbRegistration';
import { VALIDATE_SHIPPING_TYPES } from '../../../../config/service';
import { QUANTITY_TYPES } from '../../../../config/config';
import { MEMBERSHIP_ORDER_TBB_STEPS, MEMBERSHIP_ORDER_TBB_TYPE } from '../../../MembershipOrderTbb/MembershipOrderTbbContext';
import { pushSubscriptionStartedEvent } from '../../../../utils/gtm';
import {
  SubscriptionHero,
  SubscriptionStepCard,
  SubscriptionLegal,
} from '../../shared';
import SubscriptionPaymentReview from './SubscriptionPaymentReview';

import './SubscriptionPayment.scss';

const {
  subscription: {
    payment: {
      [SUBSCRIPTION_TRACKER_TYPES.VIEW]: trackerView,
    },
  },
} = TRACKER_IDS;

const SubscriptionPayment = () => {
  const hasTrackedInitiateCheckout = useRef(false);
  const hasTrackedSubscriptionStarted = useRef(false);
  const { userData } = useFirebaseAuth();
  const {
    config: subscriptionConfig,
    subscriptionPlan,
    productPrices,
    posthog,
    isAnonymous,
    searchParams,
    optionSelected,
  } = useSubscription();

  const megaKitMembershipItemIds = getFilteredMembershipItems(SUBSCRIPTION_PAYMENT_TYPES.ONE_TIME)
    .map((m) => m.id);
  const megaKitSubtotal = getOrderSubtotal(
    MEGA_KIT_MEMBERSHIP_ORDER_ITEMS,
    productPrices,
    megaKitMembershipItemIds,
  );
  const megaKitDisplayPrice = megaKitSubtotal > 0 ? megaKitSubtotal : 98;

  const selectionSections =
    subscriptionConfig?.steps?.[SUBSCRIPTION_STEPS.SELECTION]?.sections || [];
  const optionsSection = selectionSections.find((s) => s.id === 'options');
  const selectedOption = optionsSection?.items?.find((i) => i.uid === optionSelected);
  const isMegaKit = selectedOption?.type === SUBSCRIPTION_PAYMENT_TYPES.ONE_TIME;

  const [showCheckoutSection, setShowCheckoutSection] = useState(false);
  const paymentFlowAnimRef = useRef({ showCheckoutSection, direction: 'forward' });
  if (paymentFlowAnimRef.current.showCheckoutSection !== showCheckoutSection) {
    paymentFlowAnimRef.current = {
      showCheckoutSection,
      direction: showCheckoutSection ? 'forward' : 'back',
    };
  }
  const paymentFlowDirection = paymentFlowAnimRef.current.direction;
  const paymentStepSectionRef = useRef(null);
  useSubscriptionStepScroll(paymentStepSectionRef, showCheckoutSection);
  const [products, setProducts] = useState({});
  const [address, setAddress] = useState(null);
  const [checkoutEnabled, setCheckoutEnabled] = useState(true);
  const [errorStripe, setErrorStripe] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [isLoadingClientSecret, setIsLoadingClientSecret] = useState(false);
  const [isAddressValid, setIsAddressValid] = useState(false);
  const [productsStep, setProductsStep] = useState(null);
  const [couponMessage, setCouponMessage] = useState('');
  const [couponErrorMessage, setCouponErrorMessage] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCouponCode, setAppliedCouponCode] = useState('');
  const [discountedValue, setDiscountedValue] = useState(null);
  const [isCouponApplied, setIsCouponApplied] = useState(false);

  const info = subscriptionConfig?.steps?.[SUBSCRIPTION_STEPS.PAYMENT]?.info || '';

  const fetchClientSecret = useCallback(async (
    productsData,
    addressData,
    couponId = null,
  ) => {
    setIsLoadingClientSecret(true);
    setCouponMessage('');
    setCouponErrorMessage('');

    let data;
    let createCheckoutSessionError;

    if (isMegaKit) {
      const membershipItems = getFilteredMembershipItems(SUBSCRIPTION_PAYMENT_TYPES.ONE_TIME)
        .map((item) => item.id);

      const mappedProducts = Object.entries(productsData)
        .filter(
          ([productType, quantity]) => membershipItems.includes(productType) && quantity > 0,
        )
        .map(([productType, quantity]) => {
          const item = MEMBERSHIP_OFFERED_ITEMS[productType];
          return {
            productType: item?.id,
            quantity,
            description: quantity > 1
              ? item?.quantityLabel?.[QUANTITY_TYPES.PLURAL]
              : item?.quantityLabel?.[QUANTITY_TYPES.SINGULAR],
          };
        })
        .filter((p) => p.productType);

      const result = await createOrderCheckoutSession({
        products: mappedProducts,
        shippingAddress: addressData,
        shippingPrice: 0,
        searchParams,
      });
      data = result.data;
      createCheckoutSessionError = result.error;
    } else {
      const membershipItems = getFilteredMembershipItems(SUBSCRIPTION_PAYMENT_TYPES.SUBSCRIPTION)
        .map((item) => item.id);

      const mappedProducts = Object.entries(productsData)
        .filter(
          ([productType, quantity]) => membershipItems.includes(productType) && quantity > 0,
        )
        .map(([productType, quantity]) => ({
          productType: MEMBERSHIP_OFFERED_ITEMS[productType].id,
          quantity,
        }));

      const result = await createSubscriptionCheckoutSession({
        products: mappedProducts,
        address: addressData,
        couponId,
        searchParams,
      });
      data = result.data;
      createCheckoutSessionError = result.error;
    }

    if (createCheckoutSessionError || !data?.clientSecret) {
      setErrorStripe(getGenericError());
      setClientSecret(null);
      setCheckoutEnabled(false);
      setIsLoadingClientSecret(false);
      return;
    }

    const appliedCoupon = Boolean(couponId && data.couponApplied);

    setClientSecret(data?.clientSecret ?? null);
    setErrorStripe(null);
    setIsCouponApplied(Boolean(!isMegaKit && couponId && data?.couponApplied));
    setDiscountedValue(data?.discountedValue ?? null);

    if (couponId && !isMegaKit) {
      if (appliedCoupon) {
        setCouponMessage('Coupon applied successfully');
        setCouponErrorMessage('');
      } else {
        setCouponMessage('');
        setCouponErrorMessage('Invalid coupon');
      }
    }

    setCouponCode('');
    setCheckoutEnabled(true);
    setIsLoadingClientSecret(false);
    if (!hasTrackedInitiateCheckout.current) {
      window.fbq?.('track', 'InitiateCheckout');
      hasTrackedInitiateCheckout.current = true;
    }
  }, [isMegaKit]);

  const checkoutSectionRef = useRef(null);

  const handleCheckoutClick = useCallback(() => {
    setShowCheckoutSection(true);
  }, []);

  const handleBackToReview = useCallback(() => {
    setShowCheckoutSection(false);
  }, []);

  useEffect(() => {
    if (products && address) {
      fetchClientSecret(products, address, appliedCouponCode);
    }
  }, [products, address, appliedCouponCode]);

  // addressOnly skips MembershipOrderTbb, so shipping validation never reached onAddressValidated;
  // mirror MembershipOrderTbb so Stripe enables once the address can ship.
  useEffect(() => {
    if (!address || !isValidAddress(address) || !products || Object.keys(products).length === 0) {
      setIsAddressValid(false);
      return undefined;
    }

    const filtered = { ...products };
    if (isMegaKit) {
      delete filtered[RECYCLING_PRODUCT_TYPES.VIRTUAL];
    } else {
      delete filtered[RECYCLING_PRODUCT_TYPES.TECH];
    }

    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      const { isValid } = await validateShipment(
        filtered,
        address,
        isMegaKit
          ? VALIDATE_SHIPPING_TYPES.ONE_TIME_ORDER
          : VALIDATE_SHIPPING_TYPES.MEMBERSHIP_ORDER,
      );
      if (!cancelled) {
        setIsAddressValid(isValid);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [address, products, isMegaKit]);

  useEffect(() => {
    posthog.capture(getTrackerIdEventName(trackerView));
  }, []);

  useEffect(() => {
    if (hasTrackedSubscriptionStarted.current || isMegaKit) {
      return;
    }
    hasTrackedSubscriptionStarted.current = true;
    pushSubscriptionStartedEvent(userData);
  }, [isMegaKit, userData]);

  return (
    <div className="SubscriptionPayment">
      <SubscriptionHero />
      <div ref={paymentStepSectionRef} className="SubscriptionPayment__content">
        <div
          key={showCheckoutSection ? 'checkout' : 'review'}
          className={classNames(
            'SubscriptionFlowPanel',
            paymentFlowDirection === 'back' ? 'SubscriptionFlowPanel--back' : 'SubscriptionFlowPanel--forward',
          )}
        >
          {!showCheckoutSection ? (
            <SubscriptionStepCard
              stepNumber="1"
              title="Review & Checkout"
              description={isMegaKit
                ? 'Get 4 bags and 1 tech box in one order. One-time purchase with free shipping.'
                : 'All Trashie Unlimited members get a three-pack and a tech box to kick-off their membership. Reorder anytime, as much as you want, all year long.'}
            >
              <SubscriptionPaymentReview
                isMegaKit={isMegaKit}
                onCheckout={handleCheckoutClick}
                checkoutDisabled={false}
              />
            </SubscriptionStepCard>
          ) : (
            <SubscriptionStepCard
              stepNumber="2"
              title="Delivery & Payment"
              description="Select your delivery address, add a coupon if you have one, then complete payment."
              onBack={handleBackToReview}
            >
              <div ref={checkoutSectionRef} className="SubscriptionPayment__formsDs20">
                <MembershipOrderTbbContainer
                  containerClassName="SubscriptionPayment__section"
                  defaultValues={isMegaKit
                    ? MEGA_KIT_MEMBERSHIP_ORDER_ITEMS
                    : (getSubscriptionOrderItemsFromStorage() || MEMBERSHIP_DEFAULT_ORDER_ITEMS)}
                  defaultStep={MEMBERSHIP_ORDER_TBB_STEPS.ADDRESSES_CONTAINER}
                  orderType={
                    isMegaKit
                      ? MEMBERSHIP_ORDER_TBB_TYPE.ONE_TIME
                      : MEMBERSHIP_ORDER_TBB_TYPE.MEMBERSHIP_FIRST
                  }
                  isAnonymous={isAnonymous}
                  useWhiteCards
                  hideOrderCta
                  addressOnly
                  addressFormSubmitButtonText="Next"
                  designSystemForms
                  onProductsChange={setProducts}
                  onAddressChange={setAddress}
                  onAddressValidated={setIsAddressValid}
                  onStepChange={setProductsStep}
                />
                {(productsStep === MEMBERSHIP_ORDER_TBB_STEPS.PRODUCT_SELECTION
                  || productsStep === MEMBERSHIP_ORDER_TBB_STEPS.ADDRESSES_CONTAINER) && (
                  <>
                    <div className="SubscriptionPayment__section SubscriptionPayment__section--coupon SubscriptionPayment__section--formsDs20">
                      <CouponCodeInput
                        value={couponCode}
                        onChange={setCouponCode}
                        onApply={() => setAppliedCouponCode(couponCode)}
                        disabled={isLoadingClientSecret}
                        applyDisabled={!couponCode || isLoadingClientSecret}
                        successMessage={couponMessage}
                        errorMessage={couponErrorMessage}
                        designSystem
                      />
                      {errorStripe && (
                        <div className="SubscriptionPayment__stripeError">{errorStripe}</div>
                      )}
                    </div>
                    {/*
                    Stripe Embedded Checkout requires a client secret from our API; we render
                    the payment section only once it exists so the Element mounts with valid props.
                  */}
                    {!isLoadingClientSecret && clientSecret && (
                      <div className="SubscriptionPayment__section SubscriptionPayment__section--stripe SubscriptionPayment__section--formsDs20">
                        <StripeCustomCheckout
                          clientSecret={clientSecret}
                          fullValue={
                            isMegaKit ? megaKitDisplayPrice : (subscriptionPlan?.value || 0)
                          }
                          discountedValue={discountedValue}
                          isCouponApplied={isCouponApplied}
                          disabled={!checkoutEnabled || !isAddressValid}
                        />
                      </div>
                    )}
                    <div className="SubscriptionPayment__terms">
                      {info && (
                        <Markdown>{info}</Markdown>
                      )}
                      By completing this purchase, you agree to our
                      &nbsp;
                      <a
                        href={MEMBERSHIP_TERMS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="View terms of service"
                      >
                        Terms of service
                      </a>
                      .
                    </div>
                  </>
                )}
              </div>
            </SubscriptionStepCard>
          )}
        </div>
      </div>
      <SubscriptionLegal />
    </div>
  );
};

export default SubscriptionPayment;
